// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Re-run All Dialog for I-9 documents
 *
 * Re-queues every eligible document in the current tab (all pages, server-side), not
 * just the loaded page. Documents already in flight are skipped by the server. On the
 * match tabs the reviewer can also exclude documents by fail type — e.g. skip ones
 * categorized "Not an I-9 Form" that re-running won't fix.
 */
import { useEffect, useState } from 'react'
import type { FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Checkbox,
  Label,
} from '@/components/ui'
import { RotateCcw } from 'lucide-react'
import { FAIL_TYPES, type FailType } from './FailTypeSelect'
import { useCropOverride, type CropValues } from './CropOverride'

interface RetryAllDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tabLabel: string
  documentCount: number | null
  // Whether the fail-type exclusion controls are relevant for this tab.
  showExclusions: boolean
  isRetrying: boolean
  onConfirm: (excludeFailTypes: FailType[], crop: CropValues | null, skipSameBand: boolean) => void
}

export const RetryAllDialog: FC<RetryAllDialogProps> = ({
  open,
  onOpenChange,
  tabLabel,
  documentCount,
  showExclusions,
  isRetrying,
  onConfirm,
}) => {
  const [excluded, setExcluded] = useState<Set<FailType>>(new Set())
  const [skipSameBand, setSkipSameBand] = useState(true)
  const { crop, isValid, element: cropFields } = useCropOverride(open)

  // Reset exclusions and the skip toggle whenever the dialog reopens.
  useEffect(() => {
    if (open) {
      setExcluded(new Set())
      setSkipSameBand(true)
    }
  }, [open])

  const toggle = (failType: FailType) => {
    setExcluded((prev) => {
      const next = new Set(prev)
      if (next.has(failType)) next.delete(failType)
      else next.add(failType)
      return next
    })
  }

  const countLabel =
    documentCount !== null ? `${documentCount.toLocaleString()} ${documentCount === 1 ? 'document' : 'documents'}` : 'all documents'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            Re-run all — {tabLabel}
          </DialogTitle>
          <DialogDescription>
            Re-queue {countLabel} in this tab for processing. Documents already being
            processed are skipped automatically.
          </DialogDescription>
        </DialogHeader>

        {showExclusions && (
          <div className="space-y-3 py-4">
            <Label>Exclude fail types</Label>
            <p className="text-xs text-muted-foreground">
              Documents with a selected fail type won&apos;t be re-run.
            </p>
            <div className="space-y-2">
              {FAIL_TYPES.map((failType) => (
                <div key={failType} className="flex items-center gap-2">
                  <Checkbox
                    id={`exclude-${failType}`}
                    checked={excluded.has(failType)}
                    onCheckedChange={() => toggle(failType)}
                  />
                  <Label htmlFor={`exclude-${failType}`} className="font-normal">
                    {failType}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="py-4">{cropFields}</div>

        <div className="flex items-start gap-2 pb-4">
          <Checkbox
            id="skip-same-band"
            checked={skipSameBand}
            onCheckedChange={(checked) => setSkipSameBand(checked === true)}
          />
          <Label htmlFor="skip-same-band" className="font-normal">
            Skip documents already run at this band
            <span className="block text-xs text-muted-foreground">
              Re-running a document at the same crop band gives the same result. Uncheck to
              force a full re-run.
            </span>
          </Label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(Array.from(excluded), crop, skipSameBand)} disabled={isRetrying || !isValid}>
            <RotateCcw className="h-4 w-4 mr-1" />
            Re-run all
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
